import React, { ChangeEvent, KeyboardEvent, useEffect, useState } from "react";

import { TextField } from "@mui/material";
import { GridFilterOperator } from "@mui/x-data-grid";

const textFilter = (
  placeholder: string,
  label: string,
  value: string,
  validate: (value: string) => boolean,
  getValueAsString: (value: string) => string,
  onKeyUp?: (event: KeyboardEvent<HTMLInputElement>, value: string, setValue: React.Dispatch<string>) => void,
): GridFilterOperator<any, string> => {
  return {
    label: label,
    value: value,
    getApplyFilterFn: ({ field, value, operator }) => {
      if (!field || !value || !operator) return null;
      return (cellValue) => cellValue === value;
    },
    InputComponent: (props) => {
      const { item, applyValue, focusElementRef, autoFocus } = props;
      const [value, setValue] = useState(item.value ?? "");
      const [isValid, setIsValid] = useState(validate(value));

      const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        const isValid = validate(newValue);
        setValue(newValue);
        setIsValid(isValid);
        applyValue({ ...item, value: newValue, isValid });
      };

      const handleKeyUp = (event: KeyboardEvent<HTMLInputElement>) => {
        if (onKeyUp) {
          onKeyUp(event, value, setValue);
        }
      };

      useEffect(() => {
        if (item.value === undefined) {
          setValue("");
          setIsValid(false);
        }
      }, [item.value]);

      return (
        <TextField
          required
          label="Value"
          placeholder={placeholder}
          variant="standard"
          value={value}
          onChange={handleChange}
          onKeyUp={handleKeyUp}
          inputRef={focusElementRef}
          error={!isValid}
          InputLabelProps={{ shrink: true }}
          autoFocus={autoFocus}
          sx={{
            width: {
              xs: "100%",
              sm: 400,
            },
          }}
        />
      );
    },
    getValueAsString: getValueAsString,
  };
};

const csvTextFilter = (
  validate: (value: string) => boolean,
  getValueAsString: (value: string) => string,
): GridFilterOperator<any, string> => {
  const sanitiseCsvInput = (
    event: KeyboardEvent<HTMLInputElement>,
    value: string,
    setValue: React.Dispatch<string>,
  ) => {
    const backspacePressed = event.key === "Backspace";
    if (!backspacePressed) {
      setValue(value.replace(/\s+/g, "").replaceAll(/,+/g, ", "));
    }
  };

  return textFilter(
    "Input comma separated values",
    "Contains",
    "contains",
    validate,
    getValueAsString,
    sanitiseCsvInput,
  );
};

const regexTextFilter = (
  validate: (value: string) => boolean,
  getValueAsString: (value: string) => string,
): GridFilterOperator<any, string> => {
  return textFilter("Input regular expressions", "Like", "like", validate, getValueAsString);
};

export { csvTextFilter, regexTextFilter };
